import {
  MetaversePopup,
  VoiceoverInput,
  VoiceoverChromakey,
  VoiceoverPlayback,
  BroadsiderPromo,
  BroadsiderPromoOption,
  BroadsiderMenu,
  FigureViewDoubleCanvas,
  BroadsiderInventory,
  BroadsiderInventorySelector,
  BroadsiderFilters,
  BroadsiderAssets,
  ViewButton,
  GreenScreen,
  QrCodePopup,
  ModelLoading
} from '../../components';
import { StyledBroadsiderHud } from './BroadsiderHud.styled';
import { theme } from '../../theme';
import { downloadFile, textVoiceHash, convertMsToDuration, convertUrl } from '../../utils';
import { useState, useCallback, useContext } from 'react';
import { useItemsListing } from '../../hooks';
import { AppContext } from '../../providers/AppContext';
import { textToSpeech, getVoicesList } from '../../providers/TextToSpeech';
import { INSTAGRAM_FILTER_URL } from '../../constants';

const MetaverseLogo = () => {
  return (
    <div className='hud-metaverse-logo'>
      <div className='hud-metaverse-logo-border' />
      <img src='/assets/low-respng.png' />
    </div>
  );
};

const VOICE_FILE_NAME = 'voice.mp3';

const BroadsiderHud = ({ figure, promoOptions = [], metaverseData = [], /*availableVoices = [],*/ inventory = { slots: [] } }) => {
  const [playbackDuration, setPlaybackDuration] = useState('-- : --');
  const [voiceGenInProgress, setVoiceGenInProgress] = useState(false);
  const [showGreenScreen, setShowGreenScreen] = useState(false);
  const [lipsync, setLipsync] = useState(null);
  const [isPlaying, setPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [voiceIndex, setVoiceIndex] = useState(0);
  const [lastGeneratedText, setLastGeneratedText] = useState('');
  const [lastGeneratedVoice, setLastGeneratedVoice] = useState('');
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);
  const [inventoryVisible, setInventoryVisible] = useState(true);
  const [selectedInventorySlotIndex, setSelectedInventorySlotIndex] = useState(-1);
  const [showMetaverse, setShowMetaverse] = useState(false);
  const [showVoiceover, setShowVoiceover] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const { setQrCodeData, setErrorData, voices: availableVoices, setVoices, voiceoverApiKey, setVoiceoverApiKey } = useContext(AppContext);
  const [voiceoverEditMode, setVoiceoverEditMode] = useState(!voiceoverApiKey);
  const controls = {};
  const equippedWeapon = inventory.slots.find((slot) => slot.type === 'weapon').equipped;
  const weaponPath = equippedWeapon ? equippedWeapon.nftData.glb : null;
  const weaponType = equippedWeapon ? equippedWeapon.nftData.subtype : null;
  const equippedObject = inventory.slots.find((slot) => slot.type === 'object').equipped;
  const objectPath = (equippedObject && ['shield', 'equip', 'food'].includes(equippedObject.nftData.subtype)) ? equippedObject.nftData.glb : null;
  const objectType = objectPath ? equippedObject.nftData.subtype : null;
  const dronePath = (equippedObject && (equippedObject.nftData.subtype || '').startsWith('drone')) ? equippedObject.nftData.glb : null;
  const droneType = (dronePath && equippedObject.nftData.subtype === 'drone_dance')
    ? 'dance'
    : (dronePath && equippedObject.nftData.subtype === 'drone_mutatio')
      ? 'mutatio'
      : null;
  const companionPath = (equippedObject && (equippedObject.nftData.subtype === 'companion')) ? equippedObject.nftData.glb : null;

  //const armorPath = (inventory.slots.find((slot) => slot.type === 'armor').equipped || { nftData: { glb: null }}).nftData.glb;
  const armorObject = inventory.slots.find((slot) => slot.type === 'armor').equipped;
  let armorPath = null;
  if (armorObject) {
    armorPath = armorObject.nftData.glb;
    if (figure.isHenchie() && armorObject.nftData.henchie_glb) {
      armorPath = armorObject.nftData.henchie_glb;
    }
  }

  const selectEquip = () => {
    setInventoryVisible(true);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    if (selectedInventorySlotIndex >= 0) {
      controls.moveAvatarLeft();
    } else {
      controls.centerAvatar();
    }
  };
  const selectMetaverse = () => {
    setInventoryVisible(false);
    setShowMetaverse(true);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(false);
    controls.moveAvatarRight();
  };
  const selectFilters = () => {
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(false);
    setShowGreenScreen(false);
    setShowFilters(true);
    controls.centerAvatar();
  };
  const selectVoice = () => {
    setInventoryVisible(false);
    setShowMetaverse(false);
    setShowVoiceover(true);
    setShowFilters(false);
    controls.zoomToFace();
  };

  const generateAudio = async (text) => {
    setVoiceGenInProgress(true);
    try {
      const url = await textToSpeech(text, availableVoices[voiceIndex].id, voiceoverApiKey);
      return url;
    } catch (e) {
      setErrorData({ title: 'Voice API Error', description: e.message });
    } finally {
      setVoiceGenInProgress(false);
    }
  };

  const saySomething = (url) => {
    if (url) {
      controls.speak(url);
    }
  }

  const onGenerateVoiceClicked = async (text) => {
    const url = await generateAudio(text);
    if (url) {
      setLastGeneratedText(text);
      setLastGeneratedVoice(availableVoices[voiceIndex].id);
      setAudioUrl(url);
      saySomething(url);
    }
  }

  const menuOptions = [
    { color: theme.baseRed, title: 'Equip', logo: '/assets/equip.svg', logoHover: '/assets/equip_hover.svg', descriptions: ['アクションフィギュアを装備する','액션 피규어를 준비하세요','装备你的人偶'], onSelect: selectEquip },
    { color: theme.baseYellow, title: 'Explore', logo: '/assets/metaverse.svg', logoHover: '/assets/metaverse_hover.svg', descriptions: ['メタバースを探索する','메타버스 살펴보기','探索元宇宙'], onSelect: selectMetaverse },
    { color: theme.baseGreen, title: 'Create', logo: '/assets/voiceover.svg', logoHover: '/assets/voiceover_hover.svg', descriptions: ['テキスト読み上げ','텍스트를 음성으로','文字转语音'], onSelect: selectVoice },
    { color: theme.baseViolet, title: 'Files & Filters', logo: '/assets/traits.svg', logoHover: '/assets/traits_hover.svg', descriptions: ['ファイルとフィルター','파일 및 필터','文件和过滤器'], onSelect: selectFilters }
  ];

  const _promoOptions = [
    { image: '/mocks/69_hectares.svg', text: 'hectares of amazon rainforest protected by solar punks' },
    { image: '/mocks/3d_bass.svg', text: 'All models a ready to use as vrm assets in any metaverse' }
  ];

  const _metaverseData = [
    { category: 'Featured', items: [
      {
        name: 'SOLIDS by FAR',
        platform: 'Hyperfy',
        image: '/mocks/solids.png',
        platformLogo: '/mocks/hyperfy.png'
      },
      {
        name: 'Broadside Rave Bunker',
        platform: 'Hyperfy',
        image: '/mocks/bunker.png',
        platformLogo: '/mocks/hyperfy.png'
      },
      {
        name: 'dawn',
        platform: 'Decentraland',
        image: '/mocks/dawn.png',
        platformLogo: '/mocks/decentraland.png'
      }
    ]},
    { category: 'Recommended Places', items: [
      {
        name: 'Dollhouse',
        platform: 'Decentraland',
        image: '/mocks/dollhouse.png',
        platformLogo: '/mocks/decentraland.png'
      },
      {
        name: 'HELLSKY BLIMP',
        platform: 'Spatial',
        image: '/mocks/hellsky.png',
        platformLogo: '/mocks/spatial.png'
      },
      {
        name: 'META FASHION HOUSE',
        platform: 'Hyperfy',
        image: '/mocks/fashion.png',
        platformLogo: '/mocks/hyperfy.png'
      },
      {
        name: 'dawn',
        platform: 'Decentraland',
        image: '/mocks/dawn.png',
        platformLogo: '/mocks/decentraland.png'
      },
      {
        name: 'SOLIDS by FAR',
        platform: 'Hyperfy',
        image: '/mocks/solids.png',
        platformLogo: '/mocks/hyperfy.png'
      }
    ]},
  ];

  const selectOption = (option) => {
    const newIndex = menuOptions.findIndex((e) => e.title == option.title);
    if (newIndex !== selectedMenuIndex) {
      if (option.onSelect) option.onSelect();
      setSelectedMenuIndex(newIndex);
    }
  }

  const handleAvatavi = () => {
    setQrCodeData({ url: convertUrl(figure.getVrmSource()), label: 'Scan\nwith Your\nAvatavi App' });
  }

  const handleAr = () => {
    const viewerUrl = convertUrl(`/viewer/${encodeURIComponent(convertUrl(figure.getGlbSource()))}`);
    setQrCodeData({ url: viewerUrl, label: 'Scan\nwith Your\nSmartphone' });
  }

  const handleTt = () => {
    setErrorData({ title: 'Tiktok Error', description: 'Tiktok filters are not available at the moment' });
  }

  const handleInsta = () => {
    setQrCodeData({ url: INSTAGRAM_FILTER_URL, label: 'Scan\nwith Your\nSmartphone' });
  }

  const handleNewVoiceoverApiKey = async (key) => {
    try {
      setVoicesLoading(true);
      const voicesFromApi = await getVoicesList(key);
      setVoices(voicesFromApi);
      setVoiceoverApiKey(key);
      setVoiceoverEditMode(false);
    } catch (e) {
      setErrorData({ title: 'Voice API Error', description: e.message });
    } finally {
      setVoicesLoading(false);
    }
  };

  const onInventorySlotSelected = (index) => {
    setSelectedInventorySlotIndex(index);
    if (index >= 0) {
      controls.moveAvatarLeft();
    } else {
      controls.centerAvatar();
    }
  };
  return (
    <StyledBroadsiderHud>
      <div className='scroller'>
        <FigureViewDoubleCanvas
          figure={figure}
	  objectPath={objectPath}
	  weaponPath={weaponPath}
	  armorPath={armorPath}
	  dronePath={dronePath}
	  companionPath={companionPath}
          weaponType={weaponType}
          objectType={objectType}
          droneType={droneType}
          width='100%'
          height='100%'
          controls={controls}
	  onLipsyncCreated={(ls) => setLipsync(ls)}
          onPlaybackStart={(durationMs) => {
            setPlaying(true);
            setPlaybackDuration(convertMsToDuration(durationMs));
	  }}
          onPlaybackStop={() => setPlaying(false)} />
        <div className='hud-panel hud-panel-left hud-panel-top'>
	  <BroadsiderMenu options={menuOptions} onSelect={(option) => selectOption(option)} selectedIndex={selectedMenuIndex} />
        </div>
        <div className='hud-panel hud-panel-left hud-panel-bottom'>
	  <MetaverseLogo />
        </div>
      </div>
      {inventoryVisible && <div className='hud-panel hud-panel-right hud-panel-top hud-panel-full-height'>
        <BroadsiderInventory inventory={inventory} selectedSlotIndex={selectedInventorySlotIndex} onSlotSelected={onInventorySlotSelected}/>
        <BroadsiderPromo period={5000}>
	  {promoOptions.map((o) => (<BroadsiderPromoOption bigText={o.bigText} image={o.image} text={o.text} url={o.url}/>))}
        </BroadsiderPromo>
      </div>}
      {inventoryVisible && selectedInventorySlotIndex >= 0 && <div className='hud-panel hud-panel-right-additional hud-panel-top'>
        <BroadsiderInventorySelector slot={inventory.slots[selectedInventorySlotIndex]}/>
      </div>}
      <div className='hud-frame' />
      <div className='hud-frame-bottom' />
      {showMetaverse && <MetaversePopup content={metaverseData}/>}
      {showVoiceover && <VoiceoverInput
        onVoiceSelected={(voice) => setVoiceIndex(availableVoices.findIndex((v) => v.id === voice.id))}
        voiceoverApiKey={voiceoverApiKey}
        onSetApiKey={handleNewVoiceoverApiKey}
        voiceOptions={availableVoices}
        selectedVoiceIndex={voiceIndex}
        lastGeneratedText={lastGeneratedText}
        lastGeneratedVoice={lastGeneratedVoice}
        apiKeyInProgress={voicesLoading}
        editMode={voiceoverEditMode}
        onEditClick={() => setVoiceoverEditMode(true)}
        generationInProgress={voiceGenInProgress}
        onGenerateClicked={(text) => onGenerateVoiceClicked(text)}/>}
      {showVoiceover && <VoiceoverChromakey
        showGreenScreen={showGreenScreen}
        onToggleGreenScreen={() => setShowGreenScreen(!showGreenScreen)}/>}
      {showVoiceover && <VoiceoverPlayback
        disabled={!audioUrl}
        duration={playbackDuration}
        isPlaying={isPlaying}
        lipsync={lipsync}
        onClickPlay={() => isPlaying ? controls.stopTalking() : saySomething(audioUrl)}
        onClickDownload={() => downloadFile(audioUrl, VOICE_FILE_NAME)}/>}
      {showGreenScreen && <GreenScreen />}
      {showFilters&& <div className='hud-panel hud-panel-right hud-panel-top hud-panel-full-height'>
        <BroadsiderFilters figure={figure} onAvatavi={handleAvatavi} onAr={handleAr} onTt={handleTt} onInsta={handleInsta}/>
        <BroadsiderAssets figure={figure} />
      </div>}
    </StyledBroadsiderHud>
  );
};

export default BroadsiderHud;
